import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

export type AppRole =
  // Canonical (post-collapse) labels
  | "admin"
  | "supervisor"
  | "operator"
  | "customer_portal"
  // Legacy labels — still present on existing user_roles rows; has_role()
  // treats them as equivalents of the canonical labels above.
  | "super_admin"
  | "tenant_admin"
  | "manager"
  | "customer_admin"
  | "customer_user";

export interface RoleAssignment {
  role: AppRole;
  tenant_id: string | null;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  loading: boolean;
  authError: string | null;
  roles: RoleAssignment[];
  customerIds: string[];
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleAssignment[]>([]);
  const [customerIds, setCustomerIds] = useState<string[]>([]);

  const loadAccess = async (uid: string) => {
    // Hard cap each access query so a hung network/RLS check can never
    // freeze the loading screen. A timeout here surfaces as authError
    // instead of an infinite spinner.
    const withTimeout = <T,>(p: PromiseLike<T>, label: string, ms = 8000): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
        Promise.resolve(p).then(
          (v) => { clearTimeout(t); resolve(v); },
          (e) => { clearTimeout(t); reject(e); },
        );
      });

    const [{ data: roleRows, error: rolesError }, { data: accessRows, error: accessError }] = await Promise.all([
      withTimeout(supabase.from("user_roles").select("role, tenant_id").eq("user_id", uid), "user_roles"),
      withTimeout(supabase.from("user_customer_access").select("customer_id").eq("user_id", uid), "user_customer_access"),
    ]);

    if (rolesError || accessError) {
      const message = [rolesError?.message, accessError?.message].filter(Boolean).join(" · ");
      setAuthError(message || "Failed to load workspace access");
      logger.error("Failed to load auth access mappings", rolesError ?? accessError, {
        userId: uid,
        rolesError: rolesError?.message,
        accessError: accessError?.message,
      });
    } else {
      setAuthError(null);
    }

    setRoles((roleRows ?? []) as RoleAssignment[]);
    setCustomerIds((accessRows ?? []).map((r) => r.customer_id as string));
  };

  const refresh = async () => {
    if (session?.user) await loadAccess(session.user.id);
  };

  useEffect(() => {
    let cancelled = false;
    logger.info("[auth] bootstrap start");

    // Absolute safety net: under no circumstance should the loading screen
    // outlive this timer. If anything in the auth path hangs, we surface
    // an error and let the app proceed to the unauthenticated path.
    const hardTimeout = setTimeout(() => {
      if (cancelled) return;
      logger.error("[auth] bootstrap exceeded 12s — releasing loading state");
      setAuthError((prev) => prev ?? "Sign-in is taking longer than expected. Please refresh.");
      setLoading(false);
    }, 12_000);

    const finishBootstrap = () => {
      if (cancelled) return;
      clearTimeout(hardTimeout);
      setLoading(false);
      logger.info("[auth] bootstrap complete");
    };

    // 1) listener first
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      logger.info("[auth] state change", { event, hasSession: !!s });
      setSession(s);
      if (s?.user) {
        // Defer to avoid deadlock with the auth lock. try/finally guarantees
        // we always release the loading flag, even on RLS/network failure.
        setTimeout(async () => {
          try {
            await loadAccess(s.user.id);
          } catch (err) {
            logger.error("[auth] loadAccess (listener) failed", err);
            setAuthError(err instanceof Error ? err.message : "Failed to load workspace access");
          } finally {
            finishBootstrap();
          }
        }, 0);
      } else {
        // H5 sign-out hygiene: purge tenant scope hint from localStorage on
        // sign-out so a next sign-in on the same device cannot re-hydrate a
        // stale tenant before the server-authoritative scope resolves.
        try {
          localStorage.removeItem("wms.scope.v1");
        } catch {
          /* storage may be unavailable in some contexts; not fatal */
        }
        setAuthError(null);
        setRoles([]);
        setCustomerIds([]);
        finishBootstrap();
      }
    });

    // 2) then existing session — bounded so a hung GoTrue cannot freeze boot.
    const sessionTimeout = new Promise<{ data: { session: null }; error: Error }>((resolve) =>
      setTimeout(
        () => resolve({ data: { session: null }, error: new Error("getSession timed out after 8s") }),
        8000,
      ),
    );
    Promise.race([supabase.auth.getSession(), sessionTimeout])
      .then(async ({ data, error }) => {
        if (error) {
          logger.error("[auth] getSession failed", error);
          setAuthError(error.message);
        }
        setSession(data.session);
        if (data.session?.user) {
          try {
            await loadAccess(data.session.user.id);
          } catch (err) {
            logger.error("[auth] loadAccess (getSession) failed", err);
            setAuthError(err instanceof Error ? err.message : "Failed to load workspace access");
          }
        }
        finishBootstrap();
      })
      .catch((err) => {
        logger.error("[auth] bootstrap unexpected error", err);
        setAuthError(err instanceof Error ? err.message : "Auth bootstrap failed");
        finishBootstrap();
      });

    return () => {
      cancelled = true;
      clearTimeout(hardTimeout);
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setAuthError(null);
    setRoles([]);
    setCustomerIds([]);
  };

  const value: AuthState = {
    session,
    user: session?.user ?? null,
    loading,
    authError,
    roles,
    customerIds,
    refresh,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function isOperatorRole(r: AppRole) {
  return (
    r === "admin" ||
    r === "supervisor" ||
    r === "operator" ||
    // legacy
    r === "super_admin" ||
    r === "tenant_admin" ||
    r === "manager"
  );
}

export function isCustomerRole(r: AppRole) {
  return r === "customer_portal" || r === "customer_admin" || r === "customer_user";
}

export function primaryShell(roles: RoleAssignment[]): "app" | "portal" | "none" {
  if (roles.some((r) => isOperatorRole(r.role))) return "app";
  if (roles.some((r) => isCustomerRole(r.role))) return "portal";
  return "none";
}

// H4 closeout: the client-side `isSuperAdmin(roles)` helper was removed on
// 2026-07-08. Platform-admin privilege is now server-resolved and surfaced
// via `useScope().isSuperAdmin`. Do not reintroduce a client-side derivation
// of super-admin from role rows — see docs/architecture/tenant-isolation.md
// (Invariants 2 and 3).

export function tenantIdsWithRole(roles: RoleAssignment[], role: AppRole): string[] {
  return roles.filter((r) => r.role === role && r.tenant_id).map((r) => r.tenant_id as string);
}

export function activeTenantIds(roles: RoleAssignment[]): string[] {
  const ids = new Set<string>();
  for (const r of roles) if (r.tenant_id) ids.add(r.tenant_id);
  return [...ids];
}